import {
  AlertCircle,
  CalendarClock,
  Check,
  Clock3,
  FolderPlus,
  ListTodo,
  X,
} from "lucide-react";

import {
  useEffect,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";

import {
  ApiRequestError,
  createProject,
  createTask,
} from "./api";

import {
  APPLICATION_TIME_ZONE_LABEL,
  getDefaultSamaraDateTimeInput,
  samaraLocalDateTimeToIso,
} from "./timezone";

import type {
  Project,
  Task,
  TaskPriority,
} from "./types";

interface ModalFrameProperties {
  title: string;
  subtitle: string;
  icon: ReactNode;
  children: ReactNode;
  onClose: () => void;
}

const PROJECT_COLORS = [
  "#5D50E7",
  "#2563EB",
  "#0891B2",
  "#059669",
  "#65A30D",
  "#D97706",
  "#DC2626",
  "#DB2777",
];

function getErrorMessage(
  error: unknown,
): string {
  if (error instanceof ApiRequestError) {
    if (error.status === 401) {
      return (
        "Сессия завершена. Обнови страницу "
        + "и войди снова."
      );
    }

    return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "Не удалось сохранить данные.";
}

function ModalFrame({
  title,
  subtitle,
  icon,
  children,
  onClose,
}: ModalFrameProperties) {
  useEffect(() => {
    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow = "hidden";

    function handleKeyDown(
      event: KeyboardEvent,
    ): void {
      if (event.key === "Escape") {
        onClose();
      }
    }

    window.addEventListener(
      "keydown",
      handleKeyDown,
    );

    return () => {
      document.body.style.overflow =
        previousOverflow;

      window.removeEventListener(
        "keydown",
        handleKeyDown,
      );
    };
  }, [onClose]);

  return (
    <div
      className="creation-modal-backdrop"
      role="presentation"
      onMouseDown={onClose}
    >
      <section
        className="creation-modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(event) => {
          event.stopPropagation();
        }}
      >
        <header className="creation-modal-header">
          <div className="creation-modal-icon">
            {icon}
          </div>

          <div>
            <h2>{title}</h2>
            <p>{subtitle}</p>
          </div>

          <button
            type="button"
            className="creation-modal-close"
            onClick={onClose}
            aria-label="Закрыть окно"
          >
            <X size={20} />
          </button>
        </header>

        {children}
      </section>
    </div>
  );
}

export function CreateProjectModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (project: Project) => void;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] =
    useState("");

  const [color, setColor] =
    useState(PROJECT_COLORS[0] ?? "#5D50E7");

  const [isSubmitting, setIsSubmitting] =
    useState(false);

  const [error, setError] =
    useState<string | null>(null);

  async function handleSubmit(
    event: FormEvent<HTMLFormElement>,
  ): Promise<void> {
    event.preventDefault();

    if (
      isSubmitting
      || name.trim().length === 0
    ) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await createProject({
        name: name.trim(),

        description:
          description.trim().length > 0
            ? description.trim()
            : null,

        color,
      });

      onCreated(response.data);
    } catch (requestError: unknown) {
      setError(
        getErrorMessage(requestError),
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <ModalFrame
      title="Новый проект"
      subtitle="Объедини связанные задачи в отдельное направление."
      icon={<FolderPlus size={23} />}
      onClose={onClose}
    >
      <form
        className="creation-form"
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
      >
        {error && (
          <div
            className="creation-form-error"
            role="alert"
          >
            <AlertCircle size={18} />
            <span>{error}</span>
          </div>
        )}

        <label className="creation-field">
          <span>
            Название проекта
            <strong>*</strong>
          </span>

          <input
            type="text"
            value={name}
            onChange={(event) => {
              setName(event.target.value);
            }}
            placeholder="Например: Новый сайт"
            maxLength={160}
            autoFocus
            required
            disabled={isSubmitting}
          />
        </label>

        <label className="creation-field">
          <span>Описание</span>

          <textarea
            value={description}
            onChange={(event) => {
              setDescription(
                event.target.value,
              );
            }}
            placeholder="Кратко опиши цель проекта"
            rows={4}
            maxLength={2000}
            disabled={isSubmitting}
          />
        </label>

        <fieldset className="creation-color-field">
          <legend>Цвет проекта</legend>

          <div className="creation-color-options">
            {PROJECT_COLORS.map(
              (projectColor) => (
                <button
                  key={projectColor}
                  type="button"
                  className={[
                    "creation-color-option",
                    color === projectColor
                      ? "creation-color-option-active"
                      : "",
                  ].join(" ")}
                  style={{
                    backgroundColor:
                      projectColor,
                  }}
                  onClick={() => {
                    setColor(projectColor);
                  }}
                  aria-label={
                    `Выбрать цвет ${projectColor}`
                  }
                  disabled={isSubmitting}
                >
                  {color === projectColor && (
                    <Check size={15} />
                  )}
                </button>
              ),
            )}

            <label className="creation-custom-color">
              <input
                type="color"
                value={color}
                onChange={(event) => {
                  setColor(
                    event.target.value
                      .toUpperCase(),
                  );
                }}
                disabled={isSubmitting}
              />

              <span>Другой</span>
            </label>
          </div>
        </fieldset>

        <div className="creation-form-actions">
          <button
            type="button"
            className="creation-cancel-button"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Отмена
          </button>

          <button
            type="submit"
            className="creation-save-button"
            disabled={
              isSubmitting
              || name.trim().length === 0
            }
          >
            {isSubmitting
              ? "Создаём…"
              : "Создать проект"}
          </button>
        </div>
      </form>
    </ModalFrame>
  );
}

export function CreateTaskModal({
  projects,
  onClose,
  onCreated,
}: {
  projects: Project[];
  onClose: () => void;
  onCreated: (task: Task) => void;
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] =
    useState("");

  const [projectId, setProjectId] =
    useState(
      projects[0]
        ? String(projects[0].id)
        : "",
    );

  const [priority, setPriority] =
    useState<TaskPriority>("normal");

  const [
    plannedStartAt,
    setPlannedStartAt,
  ] = useState(
    getDefaultSamaraDateTimeInput(),
  );

  const [
    plannedDurationMinutes,
    setPlannedDurationMinutes,
  ] = useState("60");

  const [dueAt, setDueAt] =
    useState("");

  const [isSubmitting, setIsSubmitting] =
    useState(false);

  const [error, setError] =
    useState<string | null>(null);

  async function handleSubmit(
    event: FormEvent<HTMLFormElement>,
  ): Promise<void> {
    event.preventDefault();

    if (
      isSubmitting
      || title.trim().length === 0
    ) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const plannedStartIso =
        samaraLocalDateTimeToIso(
          plannedStartAt,
        );

      const dueAtIso =
        samaraLocalDateTimeToIso(dueAt);

      if (
        plannedStartIso
        && dueAtIso
        && new Date(dueAtIso).getTime()
          < new Date(
            plannedStartIso,
          ).getTime()
      ) {
        throw new Error(
          "Срок не может быть раньше начала задачи.",
        );
      }

      const durationValue =
        plannedDurationMinutes.trim();

      const durationMinutes =
        durationValue.length === 0
          ? null
          : Number(durationValue);

      if (
        durationMinutes !== null
        && (
          !Number.isSafeInteger(
            durationMinutes,
          )
          || durationMinutes < 1
          || durationMinutes > 10080
        )
      ) {
        throw new Error(
          "Длительность должна быть от 1 до 10080 минут.",
        );
      }

      const response = await createTask({
        projectId:
          projectId.length > 0
            ? Number(projectId)
            : null,

        title: title.trim(),

        description:
          description.trim().length > 0
            ? description.trim()
            : null,

        priority,

        plannedStartAt:
          plannedStartIso,

        plannedDurationMinutes:
          durationMinutes,

        dueAt: dueAtIso,
      });

      onCreated(response.data);
    } catch (requestError: unknown) {
      setError(
        getErrorMessage(requestError),
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <ModalFrame
      title="Новая задача"
      subtitle="Добавь задачу в Самарское расписание рабочего дня."
      icon={<ListTodo size={23} />}
      onClose={onClose}
    >
      <form
        className="creation-form"
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
      >
        {error && (
          <div
            className="creation-form-error"
            role="alert"
          >
            <AlertCircle size={18} />
            <span>{error}</span>
          </div>
        )}

        <label className="creation-field">
          <span>
            Название задачи
            <strong>*</strong>
          </span>

          <input
            type="text"
            value={title}
            onChange={(event) => {
              setTitle(event.target.value);
            }}
            placeholder="Что нужно сделать?"
            maxLength={255}
            autoFocus
            required
            disabled={isSubmitting}
          />
        </label>

        <label className="creation-field">
          <span>Описание</span>

          <textarea
            value={description}
            onChange={(event) => {
              setDescription(
                event.target.value,
              );
            }}
            placeholder="Дополнительные детали задачи"
            rows={3}
            maxLength={10000}
            disabled={isSubmitting}
          />
        </label>

        <div className="creation-form-grid">
          <label className="creation-field">
            <span>Проект</span>

            <select
              value={projectId}
              onChange={(event) => {
                setProjectId(
                  event.target.value,
                );
              }}
              disabled={isSubmitting}
            >
              <option value="">
                Без проекта
              </option>

              {projects.map((project) => (
                <option
                  key={project.id}
                  value={project.id}
                >
                  {project.name}
                </option>
              ))}
            </select>
          </label>

          <label className="creation-field">
            <span>Приоритет</span>

            <select
              value={priority}
              onChange={(event) => {
                setPriority(
                  event.target
                    .value as TaskPriority,
                );
              }}
              disabled={isSubmitting}
            >
              <option value="low">
                Низкий
              </option>

              <option value="normal">
                Обычный
              </option>

              <option value="high">
                Высокий
              </option>

              <option value="urgent">
                Срочный
              </option>
            </select>
          </label>
        </div>

        <div className="creation-timezone-note">
          <CalendarClock size={17} />

          <span>
            Все значения времени:
            <strong>
              {APPLICATION_TIME_ZONE_LABEL}
            </strong>
          </span>
        </div>

        <div className="creation-form-grid">
          <label className="creation-field">
            <span>Начало</span>

            <input
              type="datetime-local"
              value={plannedStartAt}
              onChange={(event) => {
                setPlannedStartAt(
                  event.target.value,
                );
              }}
              disabled={isSubmitting}
            />
          </label>

          <label className="creation-field">
            <span>
              <Clock3 size={14} />
              Длительность, минут
            </span>

            <input
              type="number"
              value={
                plannedDurationMinutes
              }
              onChange={(event) => {
                setPlannedDurationMinutes(
                  event.target.value,
                );
              }}
              min={1}
              max={10080}
              step={5}
              disabled={isSubmitting}
            />
          </label>
        </div>

        <label className="creation-field">
          <span>Срок выполнения</span>

          <input
            type="datetime-local"
            value={dueAt}
            onChange={(event) => {
              setDueAt(event.target.value);
            }}
            disabled={isSubmitting}
          />
        </label>

        <div className="creation-form-actions">
          <button
            type="button"
            className="creation-cancel-button"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Отмена
          </button>

          <button
            type="submit"
            className="creation-save-button"
            disabled={
              isSubmitting
              || title.trim().length === 0
            }
          >
            {isSubmitting
              ? "Сохраняем…"
              : "Создать задачу"}
          </button>
        </div>
      </form>
    </ModalFrame>
  );
}
